import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStatus, useLogout } from '@/hooks/useApi';
import {
  User,
  LogOut,
  HelpCircle,
  Shield,
  ChevronDown,
  Settings,
  Database,
  Scale,
  Bell
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/utils/cn';

const UserMenu: React.FC = () => {
  const navigate = useNavigate();
  const { data: authStatus } = useAuthStatus();
  const logoutMutation = useLogout();

  if (!authStatus?.user) {
    return null;
  }

  const handleLogout = () => {
    logoutMutation.mutate();
  };

  const userInitials = authStatus.user.full_name
    ? authStatus.user.full_name.split(' ').map(n => n[0]).join('').toUpperCase()
    : authStatus.user.username.substring(0, 2).toUpperCase();

  const isAdmin = authStatus.user?.roles?.includes('admin');
  const isSysAdmin = authStatus.user?.roles?.includes('sysadmin');

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className={cn(
          "flex items-center gap-2 px-3 py-2 rounded-md transition-colors",
          "hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-white"
        )}>
          <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center">
            <span className="text-sm font-medium text-white">{userInitials}</span>
          </div>
          <div className="hidden md:block text-left">
            <div className="text-sm font-medium text-white">
              {authStatus.user.full_name || authStatus.user.username}
            </div>
            <div className="text-xs text-gray-400">
              {authStatus.user.roles.join(' • ')}
            </div>
          </div>
          <ChevronDown className="h-4 w-4 text-gray-400" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        {/* User Info Section */}
        <div className="px-3 py-2">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
              <span className="text-lg font-medium text-gray-700">{userInitials}</span>
            </div>
            <div className="flex-1">
              <div className="font-medium text-gray-900">
                {authStatus.user.full_name || authStatus.user.username}
              </div>
              <div className="text-sm text-gray-500">
                {authStatus.user.email}
              </div>
              <div className="text-xs text-gray-400 mt-0.5">
                {authStatus.user?.roles?.map((role: string, index: number) => (
                  <span key={role} className="inline-flex items-center gap-1">
                    <Shield className="h-3 w-3" />
                    {role}
                    {index < (authStatus.user?.roles?.length || 0) - 1 && ' • '}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        <DropdownMenuSeparator />
        
        {/* Menu Actions */}
        <DropdownMenuItem
          onClick={() => navigate('/profile')}
          className="cursor-pointer"
        >
          <User className="h-4 w-4 mr-2" />
          <span>My Profile</span>
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={() => navigate('/settings/notifications')}
          className="cursor-pointer"
        >
          <Bell className="h-4 w-4 mr-2" />
          <span>Notification Settings</span>
        </DropdownMenuItem>

        {/* System Admin Section - only for sysadmin role */}
        {isSysAdmin && (
          <>
            <DropdownMenuSeparator />

            <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              System
            </div>

            <DropdownMenuItem
              onClick={() => navigate('/admin/import-queue')}
              className="cursor-pointer"
            >
              <Database className="h-4 w-4 mr-2" />
              <span>Import Queue</span>
            </DropdownMenuItem>

            <DropdownMenuItem
              onClick={() => navigate('/admin/balance-variance')}
              className="cursor-pointer"
            >
              <Scale className="h-4 w-4 mr-2" />
              <span>Balance Variance Report</span>
            </DropdownMenuItem>
          </>
        )}

        {/* Admin Section - for business admin role */}
        {isAdmin && (
          <>
            <DropdownMenuSeparator />

            <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Administration
            </div>

            <DropdownMenuItem
              onClick={() => navigate('/admin/users')}
              className="cursor-pointer"
            >
              <Settings className="h-4 w-4 mr-2" />
              <span>User Management</span>
            </DropdownMenuItem>
          </>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={() => navigate('/help')}
          className="cursor-pointer"
        >
          <HelpCircle className="h-4 w-4 mr-2" />
          <span>Help & Documentation</span>
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        <DropdownMenuItem
          onClick={handleLogout}
          className="cursor-pointer text-red-600 hover:text-red-700 hover:bg-red-50"
        >
          <LogOut className="h-4 w-4 mr-2" />
          <span>Logout</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserMenu;